// feedback-nav.ts — THE FEEDBACK & NAVIGATION primitive family (Styles ×
// Themes program, PR-3c).
//
// It lives in pi-stack/src/client for the reason tooltip.ts and buttons.ts do:
// this is the shared client layer all five surfaces already consume, and a
// primitive that forks between the Helm and the Bridge home is not a
// primitive. helm-bridge/src/client/feedback-nav.ts and
// helm-console/src/client/feedback-nav.ts are RE-EXPORTS, never copies.
//
// The CSS twin is the `feedback-nav-component-block` fence, authored
// byte-identically in both stylesheet homes; `primitives-nav-check.mjs`
// refuses a drift the way buttons-check does for `.ibtn`. Like that block,
// this one consumes ONLY tokens both roots speak: `--btn-*` (the intent
// ramps — PR-3c mints NO `--intent-*` seat of its own), `--font-*`, `--t-*`,
// `--ease-*`, `--focus-*` and `--sb-*`.
//
// ── the seven families ────────────────────────────────────────────────────
//   TABS          role=tablist / tab / tabpanel with a roving tabindex, plus
//                 `wireTablist` to upgrade a strip that already renders its
//                 own chips (three of the four tab strips in the product
//                 build their DOM elsewhere; rewriting them was not the fix,
//                 giving them the contract was).
//   SCROLL-SPY    the #457 config-templates side nav, generalized: one copy
//                 of the band math, the smooth-scroll SELECTION LOCK, and
//                 the reduced-motion arm.
//   CHIPS         chips, badges and status pills as ONE family on the
//                 `--btn-*` intent ramps. Colour is a signifier here exactly
//                 as it is on a button; a decorative chip colour is the
//                 defect the gate names.
//   PROGRESS      determinate + indeterminate bars, spinners, skeletons —
//                 every animating one with a static reduced-motion arm.
//   EMPTY STATE   one treatment: glyph, a display-role line, a reading-role
//                 hint, and at most one action.
//   SPLIT PANE    the draggable divider as `role="separator"` with a real
//                 keyboard contract (arrows, Home/End, Enter to reset).
//   FOCUS         `focusRingClass()` — the one focus treatment, so a
//                 primitive never hand-picks an outline.
//
// ── the keyboard contract (item H, the catalog's closing row) ─────────────
// Every primitive here documents its keys and the gate's `keyboard` arm
// proves the contract is wired, not just written:
//   tabs          ←/→ (or ↑/↓ on a vertical strip) rove, Home/End jump,
//                 Space/Enter activate. Roving tabindex: exactly one tab is
//                 tabbable. A new-tab affordance is NOT a tab and is reached
//                 by Tab, not by an arrow.
//   scroll-spy    the rows are links; Enter navigates and the lock holds the
//                 selection through the smooth scroll.
//   chips         a chip with no action is not focusable. A REMOVABLE chip's
//                 × is a real button (the icon-button primitive), so
//                 Backspace/Delete need no bespoke handler.
//   progress      role=progressbar with aria-valuenow/min/max, or
//                 aria-busy for the indeterminate arm — not focusable.
//   empty state   nothing focusable but the action, which is an .ibtn.
//   split pane    the separator is a tab stop: ←/→ (or ↑/↓) move it by one
//                 step, Shift+arrow by a page, Home/End go to the bounds,
//                 Enter restores the default. aria-valuenow tracks the
//                 first pane's percentage.

import { iconBtn, type BtnIntent } from './buttons.js';

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s: string): string => String(s).replace(/[&<>"']/g, (c) => ESC[c] as string);
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** True when the viewer asked for less motion. Every animating primitive in
 *  this file consults it in JS as well as CSS: a CSS arm cannot stop a
 *  smooth-scroll call. */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** THE focus treatment, as a class rather than a per-primitive outline. One
 *  token trio (`--focus-ring`, `--focus-ring-width`, `--focus-ring-offset`)
 *  paints every focus ring in the product. */
export const FOCUS_RING_CLASS = 'focus-ring';
export function focusRingClass(extra = ''): string {
  return [FOCUS_RING_CLASS, extra].filter(Boolean).join(' ');
}

// ───────────────────────────────────────────────────────────────── 1. TABS

export interface TabSpec {
  /** Stable id — becomes the tab's `id` (`tab-<id>`) and its data hook. */
  id: string;
  /** The visible name. Also the accessible name; there is no second string. */
  label: string;
  /** The panel's element id, for `aria-controls`. Omit only when the panel is
   *  built later — `wireTablist` fills it in when the panel appears. */
  controls?: string;
  selected?: boolean;
  /** Layout-only extra classes (the `cls:` discipline the button primitive
   *  established: colour belongs to the ramp). */
  cls?: string;
  attrs?: Record<string, string | number | boolean | null | undefined>;
}

export interface TabsSpec {
  /** The tablist's accessible name — required: an unnamed tablist is a set
   *  of anonymous buttons to a screen reader. */
  label: string;
  tabs: readonly TabSpec[];
  orientation?: 'horizontal' | 'vertical';
  /** The new-tab affordance (the assistant overlay's `+`). It is NOT a tab:
   *  it renders after the tabs, stays out of the roving set, and is reached
   *  by Tab. Before PR-3c it carried `role="tab"` with no `aria-selected`
   *  and a default tabIndex, so arrowing off the last chat tab focused it
   *  and — under activation-follows-focus — CLICKED it, opening a draft the
   *  user never asked for. */
  newTab?: { label: string; glyph?: string };
  cls?: string;
}

const attrStr = (pairs: Array<[string, string | number | boolean | null | undefined]>): string =>
  pairs
    .filter(([, v]) => v !== null && v !== undefined && v !== false)
    .map(([k, v]) => (v === true ? ` ${k}` : ` ${k}="${esc(String(v))}"`))
    .join('');

/** The HTML-string lane. */
export function tabsMarkup(spec: TabsSpec): string {
  const vertical = spec.orientation === 'vertical';
  const tabs = spec.tabs
    .map((t) => {
      const sel = !!t.selected;
      // `data-btn-role="tab"` is written LITERALLY into the tag rather than
      // through attrStr: buttons-check classifies a markup site by reading
      // the opening tag, and an attribute that only exists inside a `${…}`
      // hole is invisible to it — the site would read as an off-system
      // button instead of the tab it is.
      return `<button data-btn-role="tab"${attrStr([
        ['type', 'button'],
        ['class', ['tabset-tab', FOCUS_RING_CLASS, t.cls ?? ''].filter(Boolean).join(' ')],
        ['role', 'tab'],
        ['id', `tab-${t.id}`],
        ['data-tab', t.id],
        ['aria-selected', sel ? 'true' : 'false'],
        ['aria-controls', t.controls ?? null],
        ['tabindex', sel ? 0 : -1],
        ...Object.entries(t.attrs ?? {}),
      ])}>${esc(t.label)}</button>`;
    })
    .join('');
  const plus = spec.newTab
    ? iconBtn({
        glyph: spec.newTab.glyph ?? 'plus',
        label: spec.newTab.label,
        cls: `tabset-new ${FOCUS_RING_CLASS}`,
        attrs: { 'data-tabset-new': '1', 'data-btn-role': 'trigger' },
      })
    : '';
  return `<div${attrStr([
    ['class', ['tabset', vertical ? 'tabset-v' : '', spec.cls ?? ''].filter(Boolean).join(' ')],
    ['role', 'tablist'],
    ['aria-label', spec.label],
    ['aria-orientation', vertical ? 'vertical' : null],
  ])}>${tabs}${plus}</div>`;
}

/** The DOM lane — same contract, one implementation of it. */
export function mkTabs(spec: TabsSpec): HTMLElement {
  const host = document.createElement('div');
  host.innerHTML = tabsMarkup(spec);
  return host.firstElementChild as HTMLElement;
}

export interface TablistOpts {
  /** Selector for the tab elements inside the strip. Default `[role="tab"]`,
   *  which is right once `wireTablist` has stamped them. */
  tabSel?: string;
  /** Accessible name, stamped when the strip has none. */
  label?: string;
  orientation?: 'horizontal' | 'vertical';
  /** The class an already-rendering strip uses for the active chip. When
   *  given, `aria-selected` is derived from it rather than invented. */
  activeClass?: string;
  /** Arrowing also ACTIVATES (WAI-ARIA "automatic activation"). Default
   *  false — manual activation, because several strips in this product open
   *  a session or create a draft on activation, and a focus move must not do
   *  that. */
  follow?: boolean;
  /** Called on Space/Enter (and on arrow when `follow`). Default: click the
   *  tab, which is what every existing strip's delegated handler expects. */
  onActivate?: (tab: HTMLElement) => void;
}

export interface TablistHandle {
  /** Re-stamp roles and the roving tabindex after the strip rebuilt itself.
   *  Returns the number of tabs stamped — the measured count. */
  refresh(): number;
  destroy(): void;
}

/**
 * Give an EXISTING tab strip the tablist contract, in place.
 *
 * Three of the product's four tab strips render their own chips from their
 * own view models (the assistant overlay rebuilds the whole strip on every
 * pass; the explorer facet row and the API-docs language row build theirs
 * inline). Replacing that DOM with `mkTabs` would have been a rewrite of
 * three unrelated views; giving them the contract is the actual deliverable —
 * roles, `aria-selected` derived from the class they already toggle, ONE
 * tabbable tab, and arrow/Home/End confined to THIS tablist.
 *
 * Idempotent, and re-stamps itself through a MutationObserver so a strip that
 * wipes and rebuilds (the overlay does, every render) does not silently lose
 * its roles.
 */
export function wireTablist(strip: HTMLElement, opts: TablistOpts = {}): TablistHandle {
  const tabSel = opts.tabSel ?? '[role="tab"]';
  const vertical = opts.orientation === 'vertical';
  let stamping = false;

  const tabsOf = (): HTMLElement[] => Array.from(strip.querySelectorAll<HTMLElement>(tabSel))
    // A nested tablist's tabs belong to IT, not to us — the settings tab bar
    // holds two zone tablists and arrowing used to cross the boundary.
    .filter((t) => t.closest('[role="tablist"]') === strip);

  const refresh = (): number => {
    stamping = true;
    try {
      if (strip.getAttribute('role') !== 'tablist') strip.setAttribute('role', 'tablist');
      if (opts.label && !strip.getAttribute('aria-label')) strip.setAttribute('aria-label', opts.label);
      if (vertical) strip.setAttribute('aria-orientation', 'vertical');
      const tabs = tabsOf();
      let selected = -1;
      tabs.forEach((t, i) => {
        if (t.getAttribute('role') !== 'tab') t.setAttribute('role', 'tab');
        const on = opts.activeClass
          ? t.classList.contains(opts.activeClass)
          : t.getAttribute('aria-selected') === 'true';
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on && selected < 0) selected = i;
      });
      // Roving tabindex: exactly one tab is tabbable, and it is the selected
      // one (the first tab when nothing is selected yet).
      const rove = selected < 0 ? 0 : selected;
      tabs.forEach((t, i) => t.setAttribute('tabindex', i === rove ? '0' : '-1'));
      return tabs.length;
    } finally {
      stamping = false;
    }
  };

  const activate = (t: HTMLElement): void => {
    if (opts.onActivate) opts.onActivate(t);
    else t.click();
  };

  const onKey = (e: KeyboardEvent): void => {
    const cur = (e.target as HTMLElement | null)?.closest<HTMLElement>(tabSel);
    if (!cur || !strip.contains(cur)) return;
    const tabs = tabsOf();
    const i = tabs.indexOf(cur);
    if (i < 0) return;
    const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
    let next = -1;
    if (e.key === prevKey) next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === nextKey) next = (i + 1) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      activate(cur);
      return;
    } else return;
    e.preventDefault();
    const t = tabs[next];
    if (!t) return;
    tabs.forEach((x, n) => x.setAttribute('tabindex', n === next ? '0' : '-1'));
    t.focus();
    if (opts.follow) activate(t);
  };

  strip.addEventListener('keydown', onKey);
  let mo: MutationObserver | null = null;
  if (typeof MutationObserver === 'function') {
    mo = new MutationObserver(() => {
      if (!stamping) refresh();
    });
    mo.observe(strip, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  refresh();
  return {
    refresh,
    destroy(): void {
      mo?.disconnect();
      strip.removeEventListener('keydown', onKey);
    },
  };
}

/** Bind a tablist's tabs to their panels: `aria-controls` on the tab,
 *  `role="tabpanel"` + `aria-labelledby` on the panel, `hidden` on the rest.
 *  Separate from `wireTablist` because two strips in this product have no
 *  panel elements at all (they swap content in place). */
export function bindTabPanels(strip: HTMLElement, pairs: ReadonlyArray<{ tab: HTMLElement; panel: HTMLElement }>): void {
  pairs.forEach(({ tab, panel }, i) => {
    if (!tab.id) tab.id = `tabset-tab-${i}-${Math.random().toString(36).slice(2, 7)}`;
    if (!panel.id) panel.id = `${tab.id}-panel`;
    tab.setAttribute('aria-controls', panel.id);
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', tab.id);
    if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
  });
  void strip;
}

// ─────────────────────────────────────────────────────────── 2. SCROLL-SPY

/** The band below the pane's top edge that decides "the section you are
 *  reading". 30% of the viewport, capped — the #457 value, kept because it
 *  is the one that shipped and was tuned against the real pane. */
export const SPY_BAND_MAX_PX = 120;

export interface SpySection {
  id: string;
  /** offsetTop of the section inside the scroller. */
  top: number;
}

/**
 * THE one copy of the spy decision (DOM-free so a pure test can pin it — the
 * railGeom discipline). Generalized out of config-templates-vm.ts, which now
 * imports it: two copies of a scroll rule drift the moment one pane is tuned.
 *
 * The bottom pin is not a nicety: without it the LAST section can never win,
 * because a short final section never reaches the band.
 */
export function activeSection(
  sections: readonly SpySection[],
  scrollTop: number,
  viewH: number,
  contentH: number,
): string | null {
  if (sections.length === 0) return null;
  if (scrollTop + viewH >= contentH - 2) return sections[sections.length - 1]!.id;
  const band = scrollTop + Math.min(SPY_BAND_MAX_PX, viewH * 0.3);
  let winner = sections[0]!.id;
  for (const s of sections) {
    if (s.top <= band) winner = s.id;
    else break;
  }
  return winner;
}

export interface ScrollSpyOpts {
  /** The scrolling pane. */
  scroller: HTMLElement;
  /** Measured fresh on every sweep — sections collapse, filter and re-order. */
  sections: () => readonly SpySection[];
  /** Called when the active section changes (and once at attach). */
  onActive: (id: string | null) => void;
  /** ms before a smooth-scroll lock releases itself. */
  lockMs?: number;
}

export interface ScrollSpyHandle {
  /** Re-measure now. Returns the id it settled on. */
  sweep(): string | null;
  /** Click-to-scroll: select immediately, then scroll, holding the selection
   *  through the animation so intermediate sections cannot steal it.
   *  `instant` lands without the animation (the app's own reduce seat —
   *  `[data-reduce="1"]` — which matchMedia cannot see; the OS lane is read
   *  here regardless). */
  navigateTo(id: string, top: number, opts?: { instant?: boolean }): void;
  destroy(): void;
}

export function attachScrollSpy(opts: ScrollSpyOpts): ScrollSpyHandle {
  const { scroller } = opts;
  const lockMs = opts.lockMs ?? 1500;
  let current: string | null = null;
  let lock: { id: string; top: number } | null = null;
  let lockTimer: ReturnType<typeof setTimeout> | null = null;
  let raf = 0;

  const settle = (id: string | null): void => {
    if (id === current) return;
    current = id;
    opts.onActive(id);
  };

  const sweep = (): string | null => {
    raf = 0;
    if (lock) return current;
    const id = activeSection(opts.sections(), scroller.scrollTop, scroller.clientHeight, scroller.scrollHeight);
    settle(id);
    return id;
  };

  const schedule = (): void => {
    if (raf) return;
    raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(sweep) : 0;
    if (!raf) sweep();
  };

  const releaseLock = (): void => {
    lock = null;
    if (lockTimer) { clearTimeout(lockTimer); lockTimer = null; }
  };

  const onScroll = (): void => {
    if (lock && Math.abs(scroller.scrollTop - lock.top) < 4) releaseLock();
    schedule();
  };
  scroller.addEventListener('scroll', onScroll, { passive: true });
  scroller.addEventListener('scrollend', releaseLock);

  sweep();

  return {
    sweep,
    navigateTo(id: string, top: number, opts?: { instant?: boolean }): void {
      const clamped = clamp(top, 0, Math.max(0, scroller.scrollHeight - scroller.clientHeight));
      settle(id);
      if (opts?.instant || prefersReducedMotion()) {
        scroller.scrollTop = clamped;
        return;
      }
      lock = { id, top: clamped };
      if (lockTimer) clearTimeout(lockTimer);
      lockTimer = setTimeout(releaseLock, lockMs);
      scroller.scrollTo({ top: clamped, behavior: 'smooth' });
    },
    destroy(): void {
      if (raf) cancelAnimationFrame(raf);
      releaseLock();
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('scrollend', releaseLock);
    },
  };
}

// ────────────────────────────────────────────────────────────── 3. CHIPS

/** A chip's meaning rides the SAME four intents a button does — the ruling's
 *  "never decorative colour", applied to the label family. `muted` is the
 *  absence of meaning (a count, a neutral tag) and is the default. */
export type ChipIntent = BtnIntent | 'muted';
export type ChipTone = 'soft' | 'solid' | 'outline';

export interface ChipSpec {
  label: string;
  intent?: ChipIntent;
  /** soft (a wash — the default), solid (the ramp's fill), outline. */
  tone?: ChipTone;
  /** The leading status dot. On by default for a chip that carries an
   *  intent, off for a muted count. */
  dot?: boolean;
  /** A leading sprite glyph instead of the dot. */
  glyph?: string;
  size?: 'sm' | 'md';
  /** Layout only — colour belongs to the ramp. */
  cls?: string;
  title?: string;
  attrs?: Record<string, string | number | boolean | null | undefined>;
  /** Renders a real remove button (the icon-button primitive) inside the
   *  chip, so the affordance is focusable and named without a new mechanism. */
  removeLabel?: string;
}

type AttrPairs = Array<[string, string | number | boolean | null | undefined]>;

function chipAttrs(spec: ChipSpec): AttrPairs {
  const intent: ChipIntent = spec.intent ?? 'muted';
  const tone: ChipTone = spec.tone ?? 'soft';
  return [
    ['class', ['chip', `chip-${spec.size ?? 'md'}`, spec.cls ?? ''].filter(Boolean).join(' ')],
    ['data-chip', intent],
    ['data-chip-tone', tone],
    ['title', spec.title ?? null],
    ...Object.entries(spec.attrs ?? {}),
  ];
}

function chipInner(spec: ChipSpec): string {
  const intent: ChipIntent = spec.intent ?? 'muted';
  const dot = spec.dot ?? (intent !== 'muted' && !spec.glyph);
  const lead = spec.glyph
    ? `<svg class="icon chip-glyph" aria-hidden="true"><use href="#i-${esc(spec.glyph)}"/></svg>`
    : dot
      ? '<span class="chip-dot" aria-hidden="true"></span>'
      : '';
  const x = spec.removeLabel
    ? iconBtn({ glyph: 'x', label: spec.removeLabel, size: 'xs', cls: 'chip-x', attrs: { 'data-chip-remove': '1' } })
    : '';
  return `${lead}<span class="chip-label">${esc(spec.label)}</span>${x}`;
}

/** The HTML-string lane. */
export function chip(spec: ChipSpec): string {
  return `<span${attrStr(chipAttrs(spec))}>${chipInner(spec)}</span>`;
}

/** The ONE attribute loop both DOM lanes ride (mkChip's span, mkChipLink's
 *  anchor) — the pairs are the same pairs the string lane serialises, so the
 *  two lanes cannot drift on an attribute. */
function applyAttrs(el: HTMLElement, pairs: AttrPairs): void {
  for (const [k, v] of pairs) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') el.className = String(v);
    else el.setAttribute?.(k, v === true ? '' : String(v));
  }
}

/** The DOM lane. */
export function mkChip(spec: ChipSpec): HTMLElement {
  const el = document.createElement('span');
  applyAttrs(el, chipAttrs(spec));
  el.innerHTML = chipInner(spec);
  return el;
}

/** Re-point a rendered chip's intent (a status that changes stays one chip).
 *  Keys `dataset.chip`, so it works on a `chip()` span and a `chipLink()`
 *  anchor alike. */
export function setChipIntent(el: HTMLElement | null | undefined, intent: ChipIntent): void {
  if (el) el.dataset.chip = intent;
}

// ───────────────────────────────────────────────────────── 3b. LINK CHIPS

/** A chip that NAVIGATES. It carries the chip material (the same `data-chip`
 *  intent ramp, tone, dot/glyph, size) on an `<a href>`, never on a span with
 *  a click handler and never on a button: navigation IS an href — the
 *  browser gives an anchor middle-click / ctrl-click to a new tab, "copy link
 *  address", "open in new window", hover-URL preview, history and prefetch
 *  for free, and it is natively focusable and announced as a link, so there
 *  is no `tabindex` and no `role` to hand-roll. A button is for an action
 *  that changes state in place; the moment the verb is "go there", it is a
 *  link. `.chip.link` is the class the hand-rolled navigation chips already
 *  wear, so their material applies unchanged. */
export interface ChipLinkSpec extends ChipSpec {
  href: string;
  /** Opening in a new tab forces `rel="noopener"` (merged into `rel`). */
  target?: '_blank';
  rel?: string;
  /** The chip names the page the viewer is on → `aria-current="page"`. */
  current?: boolean;
}

/** An href whose scheme runs script is not navigation; it is refused by name
 *  rather than escaped into an attribute where it would still fire. Leading
 *  whitespace/control bytes are stripped first because browsers strip them
 *  too (`" javascript:"` navigates). */
const SCRIPT_HREF = /^[\s\u0000-\u001f]*(javascript|vbscript):/i;

function chipLinkAttrs(spec: ChipLinkSpec): AttrPairs {
  if (typeof spec.href !== 'string') {
    throw new Error(`chipLink: refused href — expected a string, got ${typeof spec.href} (a link chip navigates; give it an href or render chip())`);
  }
  const script = SCRIPT_HREF.exec(spec.href);
  if (script) {
    throw new Error(`chipLink: refused href — "${(script[1] ?? '').toLowerCase()}:" runs script, it does not navigate (href=${JSON.stringify(spec.href)})`);
  }
  const relParts = (spec.rel ?? '').split(/\s+/).filter(Boolean);
  if (spec.target === '_blank' && !relParts.includes('noopener')) relParts.push('noopener');
  return [
    ...chipAttrs(spec).map(([k, v]): AttrPairs[number] => (k === 'class' ? [k, `${String(v)} link`] : [k, v])),
    ['href', spec.href],
    ['target', spec.target ?? null],
    ['rel', relParts.length ? relParts.join(' ') : null],
    ['aria-current', spec.current ? 'page' : null],
  ];
}

/** The HTML-string lane: `<a href … class="chip chip-<size> link …">`. */
export function chipLink(spec: ChipLinkSpec): string {
  return `<a${attrStr(chipLinkAttrs(spec))}>${chipInner(spec)}</a>`;
}

/** The DOM lane — the same attribute pairs through the same loop as mkChip. */
export function mkChipLink(spec: ChipLinkSpec): HTMLAnchorElement {
  const el = document.createElement('a');
  applyAttrs(el, chipLinkAttrs(spec));
  el.innerHTML = chipInner(spec);
  return el;
}

// ──────────────────────────────────────────── 4. PROGRESS / SPINNER / SKELETON

export interface ProgressSpec {
  /** 0..1. Omit for the indeterminate arm. */
  value?: number;
  /** The accessible name. Required: an unnamed progress bar tells nobody
   *  what is progressing. */
  label: string;
  size?: 'sm' | 'md';
  intent?: BtnIntent;
  cls?: string;
}

/** A determinate bar carries role=progressbar + aria-valuenow; the
 *  indeterminate arm drops valuenow and says aria-busy instead, which is the
 *  only honest thing to report when the fraction is unknown. */
export function progressBar(spec: ProgressSpec): string {
  const det = typeof spec.value === 'number' && Number.isFinite(spec.value);
  const pct = det ? Math.round(clamp(spec.value as number, 0, 1) * 100) : 0;
  return `<div${attrStr([
    ['class', ['prog', `prog-${spec.size ?? 'md'}`, det ? '' : 'prog-indet', spec.cls ?? ''].filter(Boolean).join(' ')],
    ['role', 'progressbar'],
    ['aria-label', spec.label],
    ['data-btn', spec.intent ?? 'neutral'],
    ['aria-valuemin', det ? 0 : null],
    ['aria-valuemax', det ? 100 : null],
    ['aria-valuenow', det ? pct : null],
    ['aria-busy', det ? null : 'true'],
  ])}><span class="prog-fill"${det ? ` style="width:${pct}%"` : ''}></span></div>`;
}

/** The one spinner. `.ibtn[data-btn-state="busy"]` renders its own inside a
 *  button; this is the standalone lane (a pane waiting on a fetch). */
export function spinner(spec: { label: string; size?: 'sm' | 'md' | 'lg'; cls?: string }): string {
  return `<span${attrStr([
    ['class', ['spin', `spin-${spec.size ?? 'md'}`, spec.cls ?? ''].filter(Boolean).join(' ')],
    ['role', 'status'],
    ['aria-label', spec.label],
  ])}></span>`;
}

/** Skeleton placeholder lines. The shimmer is decoration, so the whole thing
 *  is `aria-hidden` and the pane that hosts it says `aria-busy` — a screen
 *  reader must not read six grey bars as content. The shimmer's static arm
 *  lives in the CSS fence's reduced-motion block, and the gate refuses a
 *  skeleton or progress rule that animates without one. */
export function skeleton(spec: { lines?: number; cls?: string; widths?: readonly string[] } = {}): string {
  const n = Math.max(1, spec.lines ?? 3);
  const widths = spec.widths ?? ['100%', '92%', '68%'];
  const rows = Array.from({ length: n }, (_, i) => `<span class="skel-line" style="width:${esc(widths[i % widths.length] ?? '100%')}"></span>`).join('');
  return `<div class="${['skel', spec.cls ?? ''].filter(Boolean).join(' ')}" aria-hidden="true">${rows}</div>`;
}

// ────────────────────────────────────────────────────────── 5. EMPTY STATES

export interface EmptyStateSpec {
  /** Sprite glyph. Required for the same reason the button's is: a blank
   *  glyph is this framework's signature silent failure. */
  glyph: string;
  /** The headline — rendered in the DISPLAY role. */
  title: string;
  /** One sentence of guidance — rendered in the READING role. */
  hint?: string;
  /** At most one action, and it is an icon button on an intent ramp. */
  action?: Parameters<typeof iconBtn>[0];
  cls?: string;
}

export function emptyState(spec: EmptyStateSpec): string {
  return `<div class="${['empty-state', spec.cls ?? ''].filter(Boolean).join(' ')}">` +
    `<svg class="icon empty-state-glyph" aria-hidden="true"><use href="#i-${esc(spec.glyph)}"/></svg>` +
    `<p class="empty-state-title">${esc(spec.title)}</p>` +
    (spec.hint ? `<p class="empty-state-hint">${esc(spec.hint)}</p>` : '') +
    (spec.action ? `<div class="empty-state-act">${iconBtn(spec.action)}</div>` : '') +
    '</div>';
}

// ─────────────────────────────────────────────────────────── 6. SPLIT PANES

export interface SplitPaneOpts {
  /** The flex/grid container that holds both panes. */
  container: HTMLElement;
  /** The pane whose size the divider sets. */
  first: HTMLElement;
  axis?: 'x' | 'y';
  /** Bounds as a fraction of the container. */
  min?: number;
  max?: number;
  /** The fraction restored by Enter and used when storage is empty. */
  initial?: number;
  /** localStorage key. Omitted = the split is not remembered. */
  storageKey?: string;
  /** Accessible name for the separator. */
  label?: string;
  /** Called with the new fraction after every change. */
  onChange?: (fraction: number) => void;
}

export interface SplitPaneHandle {
  /** The current fraction. */
  fraction(): number;
  set(fraction: number): void;
  destroy(): void;
}

const STEP = 0.02;
const PAGE = 0.1;

/**
 * The draggable divider, with the keyboard contract the hand-rolled ones in
 * this codebase never had. `role="separator"` + `aria-valuenow` means the
 * split is announced and adjustable without a pointer, which is the whole
 * reason this is a primitive rather than nine pointerdown handlers.
 */
export function attachSplitPane(opts: SplitPaneOpts): SplitPaneHandle {
  const { container, first } = opts;
  const vertical = opts.axis === 'y';
  const min = opts.min ?? 0.15;
  const max = opts.max ?? 0.85;
  const initial = clamp(opts.initial ?? 0.5, min, max);
  const doc = container.ownerDocument;

  const stored = ((): number | null => {
    if (!opts.storageKey) return null;
    try {
      const v = Number(localStorage.getItem(opts.storageKey));
      return Number.isFinite(v) && v > 0 ? clamp(v, min, max) : null;
    } catch {
      return null;
    }
  })();

  let frac = stored ?? initial;

  const bar = doc.createElement('div');
  bar.className = `splitter splitter-${vertical ? 'y' : 'x'} ${FOCUS_RING_CLASS}`;
  bar.setAttribute('role', 'separator');
  bar.setAttribute('tabindex', '0');
  bar.setAttribute('aria-orientation', vertical ? 'horizontal' : 'vertical');
  bar.setAttribute('aria-label', opts.label ?? 'Resize panes');
  bar.setAttribute('aria-valuemin', String(Math.round(min * 100)));
  bar.setAttribute('aria-valuemax', String(Math.round(max * 100)));

  const apply = (f: number, persist = true): void => {
    frac = clamp(f, min, max);
    const pct = `${(frac * 100).toFixed(2)}%`;
    if (vertical) first.style.height = pct;
    else first.style.width = pct;
    first.style.flex = `0 0 ${pct}`;
    bar.setAttribute('aria-valuenow', String(Math.round(frac * 100)));
    if (persist && opts.storageKey) {
      try { localStorage.setItem(opts.storageKey, String(frac)); } catch { /* private mode */ }
    }
    opts.onChange?.(frac);
  };

  const onDown = (e: PointerEvent): void => {
    e.preventDefault();
    bar.setPointerCapture(e.pointerId);
    bar.classList.add('dragging');
    const rect = container.getBoundingClientRect();
    const span = vertical ? rect.height : rect.width;
    const origin = vertical ? rect.top : rect.left;
    const move = (ev: PointerEvent): void => {
      if (!(span > 0)) return;
      apply(((vertical ? ev.clientY : ev.clientX) - origin) / span, false);
    };
    const up = (): void => {
      bar.classList.remove('dragging');
      bar.removeEventListener('pointermove', move);
      bar.removeEventListener('pointerup', up);
      bar.removeEventListener('pointercancel', up);
      apply(frac);
    };
    bar.addEventListener('pointermove', move);
    bar.addEventListener('pointerup', up);
    bar.addEventListener('pointercancel', up);
  };

  const onKey = (e: KeyboardEvent): void => {
    const back = vertical ? 'ArrowUp' : 'ArrowLeft';
    const fwd = vertical ? 'ArrowDown' : 'ArrowRight';
    const step = e.shiftKey ? PAGE : STEP;
    if (e.key === back) apply(frac - step);
    else if (e.key === fwd) apply(frac + step);
    else if (e.key === 'Home') apply(min);
    else if (e.key === 'End') apply(max);
    else if (e.key === 'Enter') apply(initial);
    else return;
    e.preventDefault();
  };

  bar.addEventListener('pointerdown', onDown);
  bar.addEventListener('keydown', onKey);
  first.after(bar);
  apply(frac, false);

  return {
    fraction: () => frac,
    set: (f: number) => apply(f),
    destroy(): void {
      bar.removeEventListener('pointerdown', onDown);
      bar.removeEventListener('keydown', onKey);
      bar.remove();
    },
  };
}
